import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { PagesService } from './pages.service';

/**
 * The Web QR subscription: its own plans, bought separately from the main
 * campaign plan, on the `/web_qr/*` endpoints of the subscription group.
 *
 * Server shape of a plan (GET /web_qr/plans):
 *   {
 *     id, name, description,
 *     billing_model: 'subscription',
 *     currency: 'AED', price_amount: 2500, price_per: 'quarter',
 *     overage_price: 300,
 *     limits:   { campaigns, participants_per_campaign, campaign_window_days },
 *     features: { verified_activity, ar, ar_limit, ai, ai_token_limit,
 *                 whatsapp_companion, reports, kyc_tier,
 *                 notification_channels: [...] },
 *     relation: 'available', is_current: false
 *   }
 *
 * Two things about that shape drive the design here:
 *
 *   - `billing_model` is the plan TYPE, not a billing period. Asking for
 *     `monthly` or `yearly` returns nothing; the period each plan bills on is
 *     its own `price_per` ('quarter' here). So the screen has no
 *     monthly/annual switch — each card states its own period.
 *   - `features` and `limits` are objects of flags and numbers, not a list of
 *     sentences. They are turned into translatable lines below rather than in
 *     the template, so the wording lives in one place.
 */

/** A plan's billing model — the API's plan type, used to filter the list. */
export type WebQrBillingModel = 'subscription';

/** One bullet on a plan card: a translation key plus its interpolations. */
export interface WebQrFeatureLine {
  key: string;
  params?: Record<string, unknown>;
}

export interface WebQrPlan {
  /** A uuid. */
  id: string;
  name: string;
  description: string;
  billingModel: string;
  currency: string;
  priceAmount: number;
  /** What the price buys — 'quarter', 'month', 'year' — as the API words it. */
  pricePer: string;
  /** Charge beyond the plan's allowance. Null when the plan has none. */
  overagePrice: number | null;
  featureLines: WebQrFeatureLine[];
  /** The API states outright whether this is the client's plan. */
  isCurrent: boolean;
  /** 'available' | 'current' | … as the API words it. */
  relation: string;
  raw?: any;
}

export interface WebQrPlansResult {
  plans: WebQrPlan[];
  currentPlanId: string | null;
  hasSubscription: boolean;
}

@Injectable({ providedIn: 'root' })
export class WebQrSubscriptionService {
  private pagesService = inject(PagesService);

  /** First present, non-blank value as a trimmed string. */
  private firstText(...values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '';
  }

  private rowsOf(res: any): any[] {
    if (Array.isArray(res)) return res;
    const list = res?.items ?? res?.data ?? res?.result ?? res?.plans ?? [];
    return Array.isArray(list) ? list : [];
  }

  /**
   * Turns a plan's `limits` and `features` objects into card bullets.
   *
   * A flag that is false is left out entirely rather than listed as absent —
   * a plan card sells what is included, and "AR: no" reads as a defect.
   */
  private buildFeatureLines(row: any): WebQrFeatureLine[] {
    const lines: WebQrFeatureLine[] = [];
    const limits = row?.limits ?? {};
    const features = row?.features ?? {};

    const num = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    // These values land inside a translated sentence, so the number pipe cannot
    // reach them in the template — 1000 would read as "1000 participants".
    const grouped = (n: number): string => n.toLocaleString('en-US');

    // The API sends bare enum words ('advanced', 'whatsapp'). Shown as-is they
    // read as raw data, so each is title-cased, with the few names that have a
    // fixed spelling kept correct.
    const properNames: Record<string, string> = {
      whatsapp: 'WhatsApp',
      browser: 'Browser',
      // The admin form labels the top KYC tier Heavy; the API word is high.
      high: 'Heavy',
      email: 'Email',
      sms: 'SMS',
      push: 'Push',
    };
    const pretty = (word: string): string => {
      const key = word.trim().toLowerCase();
      if (!key) return '';
      if (properNames[key]) return properNames[key];
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    };

    // ── Allowances ──────────────────────────────────────────────────────────
    const campaigns = num(limits.campaigns);
    if (campaigns !== null) {
      lines.push({ key: 'webQrPlans.limit-campaigns', params: { count: grouped(campaigns) } });
    }

    const participants = num(limits.participants_per_campaign);
    if (participants !== null) {
      lines.push({ key: 'webQrPlans.limit-participants', params: { count: grouped(participants) } });
    }

    const windowDays = num(limits.campaign_window_days);
    if (windowDays !== null) {
      lines.push({ key: 'webQrPlans.limit-window', params: { days: grouped(windowDays) } });
    }

    // ── Features ────────────────────────────────────────────────────────────
    if (features.verified_activity === true) {
      const limit = num(features.verified_activity_limit);
      lines.push(limit === null
        // A null limit here means unlimited, not unknown: the flag above is
        // already true, so the feature is included without a cap.
        ? { key: 'webQrPlans.feature-verified-activity' }
        : { key: 'webQrPlans.feature-verified-activity-limit', params: { count: grouped(limit) } });
    }

    if (features.ar === true) {
      const limit = num(features.ar_limit);
      lines.push(limit === null
        ? { key: 'webQrPlans.feature-ar' }
        : { key: 'webQrPlans.feature-ar-limit', params: { count: grouped(limit) } });
    }

    if (features.ai === true) {
      const tokens = num(features.ai_token_limit);
      lines.push(tokens === null
        ? { key: 'webQrPlans.feature-ai' }
        : { key: 'webQrPlans.feature-ai-tokens', params: { count: grouped(tokens) } });
    }

    if (features.whatsapp_companion === true) {
      lines.push({ key: 'webQrPlans.feature-whatsapp' });
    }

    const reports = this.firstText(features.reports);
    if (reports) {
      lines.push({ key: 'webQrPlans.feature-reports', params: { level: pretty(reports) } });
    }

    // A plan may permit several tiers; older rows held a single string.
    const kyc = (Array.isArray(features.kyc_tier) ? features.kyc_tier : [features.kyc_tier])
      .map((t: any) => pretty(this.firstText(t)))
      .filter(Boolean)
      .join(', ');
    if (kyc) {
      lines.push({ key: 'webQrPlans.feature-kyc', params: { tier: kyc } });
    }

    const channels = Array.isArray(features.notification_channels)
      ? features.notification_channels.map((c: any) => pretty(this.firstText(c))).filter(Boolean)
      : [];
    if (channels.length) {
      lines.push({ key: 'webQrPlans.feature-channels', params: { channels: channels.join(', ') } });
    }

    const overage = num(row?.overage_price);
    if (overage !== null && overage > 0) {
      lines.push({
        key: 'webQrPlans.feature-overage',
        params: { amount: grouped(overage), currency: this.firstText(row?.currency) || '' },
      });
    }

    return lines;
  }

  private normalizePlan(row: any): WebQrPlan {
    return {
      id: this.firstText(row?.id),
      name: this.firstText(row?.name, row?.plan_name, row?.title) || `Plan #${row?.id ?? ''}`.trim(),
      description: this.firstText(row?.description, row?.details),
      billingModel: this.firstText(row?.billing_model),
      currency: this.firstText(row?.currency) || 'USD',
      priceAmount: Number(row?.price_amount ?? row?.price ?? 0) || 0,
      pricePer: this.firstText(row?.price_per),
      overagePrice: row?.overage_price === null || row?.overage_price === undefined
        ? null
        : Number(row.overage_price) || 0,
      featureLines: this.buildFeatureLines(row),
      isCurrent: row?.is_current === true,
      relation: this.firstText(row?.relation),
      raw: row,
    };
  }

  /**
   * GET /web_qr/plans?billing_model=…
   *
   * Answers { has_subscription, current_plan_id, total, items }.
   */
  getPlans(billingModel: WebQrBillingModel = 'subscription'): Observable<WebQrPlansResult> {
    return this.pagesService.getAll('WEB_QR_PLANS', { billing_model: billingModel }).pipe(
      timeout(15000),
      map((res: any) => ({
        plans: this.rowsOf(res)
          // Inactive plans are not offered, the same rule the main plan list
          // uses. Rows without a status are treated as offerable.
          .filter((row: any) => {
            const status = String(row?.status ?? '').trim().toLowerCase();
            return !status || status === 'active';
          })
          .map((row: any) => this.normalizePlan(row))
          .filter((plan: WebQrPlan) => plan.id !== '')
          .sort((a: WebQrPlan, b: WebQrPlan) => a.priceAmount - b.priceAmount),
        currentPlanId: this.firstText(res?.current_plan_id) || null,
        hasSubscription: res?.has_subscription === true,
      }))
    );
  }

  /**
   * POST /web_qr/subscribe — returns Stripe's checkout url to redirect to.
   * The urls tell Stripe where to send the client back.
   */
  subscribe(planId: string, successUrl: string, cancelUrl: string): Observable<any> {
    return this.pagesService.create('WEB_QR_SUBSCRIBE', {
      plan_id: planId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }).pipe(timeout(20000));
  }

  /** POST /web_qr/renew — same shape as subscribe, for an existing plan. */
  renew(planId: string, successUrl: string, cancelUrl: string): Observable<any> {
    return this.pagesService.create('WEB_QR_RENEW', {
      plan_id: planId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }).pipe(timeout(20000));
  }

  /**
   * POST /web_qr/confirm_payment — turns a completed Stripe checkout session
   * into an active subscription.
   *
   * Stripe itself only redirects the client back; nothing is activated until
   * this is called with the session id it appends to the success url. So the
   * return page must call it, not the page that started the checkout — by then
   * the browser has already left for Stripe.
   */
  confirmPayment(sessionId: string): Observable<any> {
    return this.pagesService.create('WEB_QR_CONFIRM_PAYMENT', {
      session_id: sessionId || '',
    }).pipe(timeout(20000));
  }

  /**
   * POST /web_qr/cash_payment — records a request to pay in cash. It does not
   * activate anything; an admin approves it separately.
   */
  requestCashPayment(planId: string, note: string): Observable<any> {
    return this.pagesService.create('WEB_QR_CASH_PAYMENT', {
      plan_id: planId,
      note: note || '',
    }).pipe(timeout(20000));
  }

  /**
   * GET /web_qr/my_subscription — the client's Web QR plan with usage against
   * its caps. Only the Web QR one: the main plan is read from MY_SUBSCRIPTION.
   */
  getMySubscription(): Observable<any> {
    return this.pagesService.getAll('WEB_QR_MY_SUBSCRIPTION').pipe(timeout(15000));
  }
}
